/**
 * Pages are reused inside hub tabs and object-page tabs (docs/specs/control-center-navigation.md).
 * Such a page keeps reading and writing its own query parameters, while the container owns a
 * few of its own (`tab`, `sub`). `useSearchParams` here is a drop-in for react-router's hook:
 *
 * - outside a container it is react-router's hook, unchanged;
 * - inside one, the page never sees the container's keys, and writing its parameters (even a
 *   "reset" to an empty set) keeps them, so the tab stays open;
 * - a page's own `tab` parameter is stored in the URL as `pane`;
 * - `fixed` parameters (e.g. `registry` on a registry's page) are always seen and never written.
 *
 * `EmbeddedPage` also tells `PageHeader` to render as a section heading, not a second page title.
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { createSearchParams, useSearchParams as useRouterSearchParams, type SetURLSearchParams, type URLSearchParamsInit } from 'react-router';

interface ParamScope {
  /** URL keys owned by the container. */
  reserved: readonly string[];
  fixed: Readonly<Record<string, string>>;
  /** Name of the tab showing the page; a page title equal to it is not repeated. */
  label?: string;
}

const ScopeContext = createContext<ParamScope | null>(null);

/** True inside a hub tab or an object-page tab: the page is a section, not the whole page. */
export function useEmbedded(): boolean {
  return useContext(ScopeContext) !== null;
}

/**
 * A parameter the container fixes (e.g. `registry` on a registry's page), or undefined. Pages
 * use it to leave out a filter or a column that would only repeat the object being viewed.
 */
export function useFixedParam(key: string): string | undefined {
  return useContext(ScopeContext)?.fixed[key];
}

/** The name of the tab the page is shown in, if any. */
export function useEmbeddedLabel(): string | undefined {
  return useContext(ScopeContext)?.label;
}

const INNER_TAB = 'tab';
const URL_PANE = 'pane';

function toInner(url: URLSearchParams, scope: ParamScope): URLSearchParams {
  const inner = new URLSearchParams();
  for (const [k, v] of url) {
    if (scope.reserved.includes(k) || k in scope.fixed) continue;
    if (k === INNER_TAB) continue;
    inner.append(k === URL_PANE ? INNER_TAB : k, v);
  }
  for (const [k, v] of Object.entries(scope.fixed)) inner.set(k, v);
  return inner;
}

function toUrl(inner: URLSearchParams, current: URLSearchParams, scope: ParamScope): URLSearchParams {
  const url = new URLSearchParams();
  for (const k of scope.reserved) for (const v of current.getAll(k)) url.append(k, v);
  for (const [k, v] of inner) {
    // The page's own `tab` is renamed first: the container always reserves `tab`, so checking
    // the page's name against `reserved` would drop every tab change the page makes.
    const key = k === INNER_TAB ? URL_PANE : k;
    if (k in scope.fixed || scope.reserved.includes(key)) continue;
    url.append(key, v);
  }
  return url;
}

export function useSearchParams(): [URLSearchParams, SetURLSearchParams] {
  const scope = useContext(ScopeContext);
  const [url, setUrl] = useRouterSearchParams();
  const inner = useMemo(() => (scope ? toInner(url, scope) : url), [url, scope]);
  const set = useCallback<SetURLSearchParams>(
    (next, opts) => {
      if (!scope) return setUrl(next, opts);
      setUrl((prevUrl) => {
        const prevInner = toInner(prevUrl, scope);
        const value: URLSearchParamsInit | undefined = typeof next === 'function' ? next(prevInner) : next;
        return toUrl(createSearchParams(value), prevUrl, scope);
      }, opts);
    },
    [scope, setUrl],
  );
  return [inner, scope ? set : setUrl];
}

/** Renders a reused page inside a container that owns `reserved` URL keys. */
export function EmbeddedPage({ reserved, fixed, label, children }: { reserved: readonly string[]; fixed?: Record<string, string>; label?: string; children: ReactNode }) {
  const fixedKey = JSON.stringify(fixed ?? {});
  const reservedKey = reserved.join(',');
  // oxlint-disable-next-line react/exhaustive-deps -- keyed on the serialised values, not the object identities.
  const value = useMemo<ParamScope>(() => ({ reserved: reservedKey.split(','), fixed: JSON.parse(fixedKey) as Record<string, string>, label }), [reservedKey, fixedKey, label]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}
